import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { translateFr as tfr } from "@/i18n";

const STAGE_COLORS = {
  "Repéré": "#64748B",
  "Contacté": "#3B82F6",
  "En discussion": "#F59E0B",
  "Mandat signé": "#22C55E",
  "Placé": "#14B8A6",
};

export default function FunnelCard({ funnel, loading }) {
  const max = Math.max(1, ...(funnel || []).map((s) => s.count || 0));
  return (
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-6 flex flex-col shadow-sm dark:shadow-none" data-testid="perf-funnel-card">
      <h3 className="text-base font-semibold tracking-tight">{tfr("Funnel de conversion")}</h3>
      <p className="text-xs text-muted-foreground mt-0.5 mb-4">{tfr("Analysez les transitions de votre pipeline sur la période.")}</p>
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-11 rounded-lg bg-muted/60 animate-pulse" />)}</div>
      ) : (
        <div className="space-y-3 sm:space-y-2.5 flex-1" role="list" aria-label={tfr("Funnel de conversion du pipeline")}>
          {(funnel || []).map((s, i) => {
            const color = STAGE_COLORS[s.stage] || "#64748B";
            const width = Math.max(6, Math.round((100 * (s.count || 0)) / max));
            const bar = (
              <div className="h-2 rounded-full bg-secondary overflow-hidden" aria-hidden>
                <div className="h-full rounded-full" style={{ width: `${width}%`, background: color }} />
              </div>
            );
            const rate = s.conversion == null
              ? <span className="text-muted-foreground">Base</span>
              : <span className={s.conversion >= 50 ? "text-emerald-600 dark:text-emerald-400" : s.conversion >= 30 ? "text-amber-600 dark:text-amber-400" : "text-rose-600 dark:text-rose-400"}>{s.conversion}%</span>;
            const time = <span className="text-muted-foreground">{s.avg_days != null ? `${s.avg_days} ${tfr("j")}` : "—"}</span>;
            return (
              <div key={s.stage} role="listitem" data-testid={`perf-funnel-${i}`}>
                {/* Desktop / tablet: single row */}
                <div className="hidden sm:flex items-center gap-3">
                  <span className="h-3.5 w-1 rounded-full shrink-0" style={{ background: color }} aria-hidden />
                  <span className="text-xs font-medium text-foreground w-[104px] shrink-0">{tfr(s.stage)}</span>
                  <span className="text-sm font-semibold tabular-nums w-7 shrink-0 text-right" data-testid={`perf-funnel-count-${i}`}>{s.count}</span>
                  <div className="flex-1 min-w-0">{bar}</div>
                  <span className="w-[44px] text-right text-xs font-medium tabular-nums shrink-0">{rate}</span>
                  <span className="w-[38px] text-right text-[11px] tabular-nums shrink-0">{time}</span>
                </div>
                {/* Mobile: stacked */}
                <div className="sm:hidden">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-1 rounded-full shrink-0" style={{ background: color }} aria-hidden />
                    <span className="text-xs font-medium text-foreground">{tfr(s.stage)}</span>
                    <span className="ml-auto text-sm font-semibold tabular-nums">{s.count}</span>
                  </div>
                  <div className="mt-1.5">{bar}</div>
                  <div className="mt-1 flex items-center justify-between text-xs font-medium tabular-nums">
                    {rate}
                    <span className="text-[11px]">{time}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4 pt-3 border-t border-border">
        <span className="text-[10px] text-muted-foreground">{tfr("Taux · Temps moyen dans l'étape")}</span>
        <Link to="/pipeline" className="inline-flex items-center gap-1 text-xs font-medium text-athlynx hover:text-athlynx/80 transition-colors min-h-[32px] sm:min-h-0" data-testid="perf-funnel-pipeline-link">
          {tfr("Voir le pipeline complet")} <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </section>
  );
}
